import { MEETING_NAME_MAPPING } from '@commenda/api';
import {
  Button,
  CountryCode,
  DataTable,
  FlagComponent,
  Icons,
} from '@commenda/kakashi';
import { UserTypes } from '@commenda/types/src/chat-room/types';
import { IMeeting } from '@commenda/types/src/meeting/types';
import { MeetingModel } from '@commenda/utils';
import { Country } from 'country-state-city';

const formatMeetingDateTime = (dateInput: Date | string) => {
  if (!dateInput) return { date: '', time: '' };

  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;

  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const month = months[date.getMonth()];
  const day = date.getDate();
  const year = date.getFullYear();

  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours %= 12;
  hours = hours || 12;

  return {
    date: `${month} ${day}, ${year}`,
    time: `${hours}:${minutes} ${ampm}`,
  };
};

const ScheduledMeetingsTable = ({
  meetingBookings,
  userType,
  takeActionCallback,
  takeActionButton = false,
  actionButtonText = 'Take action',
}: {
  meetingBookings: IMeeting[];
  userType: UserTypes;
  takeActionCallback?: (data: any) => Promise<void>;
  takeActionButton?: boolean;
  actionButtonText?: string;
}) => {
  const columns = [];

  const joinButtonColumn = {
    id: 'action',
    accessorKey: 'meetingUrl',
    header: 'Meeting Link',
    accessorFn: (row: IMeeting) => row.meetingUrl,
    cell: ({ row }: { row: { original: IMeeting } }) => (
      <div className="flex space-x-2">
        {row.original.meetingUrl && (
          <Button
            variant="secondary"
            size="sm"
            onClick={async () => {
              window.open(row.original.meetingUrl, '_blank');
            }}
          >
            Join
          </Button>
        )}
      </div>
    ),
  };

  const actionButton = {
    id: 'actionButton',
    accessorKey: 'actionButton',
    header: 'Action',
    accessorFn: (row: IMeeting) => null,
    cell: ({ row }: { row: { original: IMeeting } }) => (
      <div className="flex space-x-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={async () => takeActionCallback(row.original)}
        >
          {actionButtonText}
        </Button>
      </div>
    ),
  };

  const scheduledForColumn = {
    id: 'scheduledFor',
    accessorKey: 'bookingDate',
    accessorFn: (row: IMeeting) => null,
    header: ({ column }) => {
      const isSortedInAscendingManner = column.getIsSorted() === 'asc';
      const SortingIcon =
        column.getIsSorted() === false
          ? Icons.ArrowDown
          : isSortedInAscendingManner
            ? Icons.ArrowDown
            : Icons.ArrowUp;

      return (
        <div className="flex items-center gap-1">
          <span>Scheduled for</span>
          <SortingIcon
            className="h-4 w-4 cursor-pointer"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          />
        </div>
      );
    },
    cell: ({ row }: { row: { original: IMeeting } }) => (
      <div>
        {row.original.startTime &&
          `${formatMeetingDateTime(row.original.startTime).date} | ${formatMeetingDateTime(row.original.startTime).time}`}
      </div>
    ),
  };

  const accountantColumns = [
    {
      id: 'companyName',
      accessorKey: 'companyName',
      accessorFn: (row) => null,
      header: 'Company Name',
      cell: ({ row }: { row: { original: IMeeting } }) => {
        const { corporation } = row.original;
        if (!corporation) return null;
        return (
          <div className="flex items-center gap-3">
            <FlagComponent
              country={corporation.incorporationCountry as CountryCode}
              countryName={
                Country.getCountryByCode(corporation.incorporationCountry).name
              }
            />
            <span className="text-text-tertiary txt-small-bold">
              {corporation?.companyLegalName}
            </span>
          </div>
        );
      },
    },
    {
      id: 'pointOfContact',
      accessorKey: 'bookingByUser.name',
      accessorFn: (row) => null,
      header: 'Point of contact',
      cell: ({ row }: { row: { original: IMeeting } }) => {
        return <div>{row.original?.bookingByUser?.name}</div>;
      },
    },
  ];
  const clientColumns = [
    {
      id: 'meetingName',
      accessorKey: 'meetingName',
      accessorFn: (row) => null,
      header: 'Meeting Name',
      cell: ({ row }: { row: { original: IMeeting } }) => {
        return (
          <div>{MEETING_NAME_MAPPING[row.original.type] ?? 'Meeting'}</div>
        );
      },
    },

    {
      id: 'bookedWithUser',
      accessorKey: 'bookedWithUser.name',
      accessorFn: (row) => null,
      header: 'Accountant',
    },
  ];

  if (userType === UserTypes.ACCOUNTANT) {
    columns.push(...accountantColumns);
  } else if (userType === UserTypes.CLIENT) {
    columns.push(...clientColumns);
  } else {
    const bookingUserColumns = [
      {
        accessorKey: 'bookingByUser.name',
        header: 'Booked by',
      },
    ];
    columns.push(...accountantColumns);
    columns.push(...bookingUserColumns);
    columns.push(...clientColumns);
  }

  columns.push(scheduledForColumn);
  columns.push(joinButtonColumn);
  if (takeActionButton) {
    columns.push(actionButton);
  }

  const allMeetingBookings =
    meetingBookings && meetingBookings.length ? meetingBookings : [];
  const filteredMeetings = MeetingModel.filterPastMeetings(
    allMeetingBookings,
    userType
  );

  return (
    <div className="overflow-hidden rounded-lg mb-4">
      <DataTable
        columnGroups={[
          {
            id: 'companyName',
            columns,
          },
        ]}
        data={filteredMeetings}
        tableClassName="border-collapse w-full"
        headerClassName="bg-bg-tertiary"
        tableHeadClassName="px-6 py-3 text-left txt-extra-small-medium text-text-tertiary tracking-wider"
        rowClassName={(row) => 'hover:bg-gray-50'}
        tableCellClassName="px-6 py-4 whitespace-nowrap text-text-tertiary txt-small-regular"
      />
    </div>
  );
};

export { ScheduledMeetingsTable };
